import ActionListStatusGroup from '@/components/ActionListCompletedActions';
import ActionRow from '@/components/ActionRow';
import AddActionButton from '@/components/AddActionButton';
import Callout from '@/components/Callout';
import CaptureList from '@/components/CaptureList';
import CategorySelector from '@/components/CategorySelector';
import ConfirmDeleteBlockModal from '@/components/ConfirmDeleteBlockModal';
import CreateBlockModal from '@/components/CreateBlockModal';
import DeleteBlockModal from '@/components/DeleteBlockModal';
import DeleteBulkActionsModal, { modeValues } from '@/components/DeleteBulkActionsModal/DeleteBulkActionModal';
import DndContainer from '@/components/DndContainer';
import { ActionList, DroppableContainer } from '@/components/DragAndDrop';
import ActionListItem from '@/components/DragAndDrop/ActionListItem';
import DnDBlockItem from '@/components/DragAndDrop/DnDBlockItem';
import CalendarProvider from '@/components/DragAndDropCalendar/provider';
import DuplicateBlockModal from '@/components/DuplicateBlockModal';
import DynamicSegmentedButton from '@/components/DynamicSegmentedButton';
import EditBlockModal from '@/components/EditBlockModal';
import EmptyActionList from '@/components/EmptyActionList';
import EmptyCategories from '@/components/EmptyCategories';
import EmptyCategoryCallout from '@/components/EmptyCategoryCallout';
import ScheduleEventModal, { ScheduleEventDateTime } from '@/components/MyPlanStep/ScheduleEventModal';
import Timeline from '@/components/MyPlanStep/Timeline';
import WeeklyCalendar, { EventsPerDayType } from '@/components/MyPlanStep/WeeklyCalendar';
import WeeklyEventItem from '@/components/MyPlanStep/WeeklyCalendar/WeeklyEventItem';
import RecurringInBlockModal from '@/components/RecurringEventModal/RecurringInBlockModal';
import RecurringEventModal, { EventOptions } from '@/components/RecurringEventModal/RecurringEventModal';
import ThreeDotsLaterButton from '@/components/ThreeDotsLaterButton';
import UncompletedActionsModal from '@/components/UncompletedActionsModal';
import {
  ALL_ACTIONS,
  COMPLETE,
  INCOMPLETE,
  IN_PROGRESS,
  LOAD_ACTIONS_PERIOD,
  UNPLANNED_ACTIONS,
  UNSCHEDULED_ACTIONS,
} from '@/constants/action';
import getPlannerMotionVariants from '@/constants/plannerMotionVariants';
import { RecurrenceRule } from '@/gql/events/types';
import { keys } from '@/gql/global/keys';
import useCaptureListData from '@/hooks/useCaptureListData';
import { ActionTimeChangeProps } from '@/pages/WeeklyPlan/Schedule';
import { useCreateAction, useUpdateAction } from '@/services/action/hooks';
import { useCategories } from '@/services/categories/hooks';
import { useCronofyProfiles } from '@/services/cronofy/hooks';
import { useCreateEvent as useEvent, useExternalEvents, useUpdateEventByPk } from '@/services/events/hooks';
import { queryClient } from '@/services/graphql/queryClient';
import {
  completeActionsOfBlock,
  onMutateBlockStatus,
  refetchBlocks,
  removeIncompleteActionsFromBlock,
  useActionsBlocksAndEvents,
  useAddActionToBlock,
  useCompleteBlockStatus,
  useCreateEvent,
  useDeleteBlock,
  useDeleteEventAndUpdateAction,
  useDeleteEventAndUpdateBlockAction,
  useIncompleteBlockStatus,
  useRemoveActionFromBlock,
  useUpdateActionsCategoryOrder,
  useUpdateBlockActionsBlockOrder,
} from '@/services/myPlan/hooks';
import useIsDailyPlanPage from '@/services/plans/hooks/useIsDailyPlanPage';
import { useWeeklyPlanId } from '@/services/plans/hooks/useWeeklyPlan';
import { useActionModal } from '@/stores/useActionModal';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import useToggleExpandCalendar from '@/stores/useToggleExpandCalendar';
import { IconCategories, IconChevronDown, IconPlus } from '@/theme/icons';
import { Action, ProgressStatus, isPlannedAction } from '@/types/actions';
import { Block } from '@/types/block';
import { MyEvent } from '@/types/calendar';
import { ActionEventType, CategoryActionType } from '@/types/myPlan';
import {
  filterIncompleteActions,
  getActionById,
  getActionsByCategoryId,
  getIncompleteActions,
  getIncompleteActionsByBlockId,
  getTotalDurationOfActions,
  getTotalDurationOfActionsInRange,
  getTotalDurationOfPreferredActions,
  getTotalDurationOfPreferredActionsInRange,
  refetchActions,
} from '@/utils/action';
import { getBlockById } from '@/utils/block';
import {
  computeRecurrenceExceptionDates,
  endOfTheWeek,
  fastForwardRecurrenceCursor,
  formatDateToString,
  getDaysWithinDatesRange,
  getExceptionDates,
  getGMTOffset,
  getIncrementInterval,
  isExceptionDate,
  isValidWeeklyDay,
  localDateToUTC,
  normalizeRecurrenceRuleUntil,
  shouldIncrementByWeek,
  startOfTheWeek,
  utcToLocalDate,
} from '@/utils/calendar';
import { actionHasRecurrenceForbiddenInBlock } from '@/utils/blockRecurringPolicy';
import { findContainerById, getIdx, getItemById } from '@/utils/dnd';
import { getValueInRecurrenceRule, validateRecurrence } from '@/utils/events';
import { isUncategorized } from '@/utils/index';
import {
  ItemType,
  createActionEvent,
  formatActions,
  getEventByActionId,
  getEventById,
  groupActionsByBlockId,
  groupEventsByWeekDay,
  makeEvents,
} from '@/utils/myplan';
import rem from '@/utils/rem';
import { invalidateQueries } from '@/utils/tanStackQuery';
import { trackActionPlannedByDrag, trackActionScheduledByDrag, trackBulkActionCompleted } from '@/utils/tracking';
import {
  Accordion,
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  useBoolean,
  useDisclosure,
} from '@chakra-ui/react';
import {
  CollisionDetection,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  UniqueIdentifier,
  closestCenter,
  defaultDropAnimationSideEffects,
  getFirstCollision,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { add, addDays, addMonths, addWeeks, addYears, endOfDay, isAfter, isBefore, isEqual, startOfDay } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { isEmpty, isNull, isUndefined, orderBy } from 'lodash';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { sortBlocksByCompletion } from './helpers';

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

const SHOW_PAST_ACTIONS_FLAG = true;

function MyWeeklyPlan() {
  const { t } = useTranslation('WeeklyPlan');
  const [selectedSegmented, setSelectedSegmented] = useState(ALL_ACTIONS);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeItemCameFrom, setActiveItemCameFrom] = useState('');
  const [tempBlockActionId, setTempBlockActionId] = useState<string | null>(null);
  const [tempCategoryId, setTempCategoryId] = useState<string | null>(null);
  const [tempProjectId, setTempProjectId] = useState<string | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<Block | null>(null);
  const [eventToUnplan, setEventToUnplan] = useState<MyEvent | null>(null);
  const [newTimeChanged, setNewTimeChanged] = useState<ActionTimeChangeProps>();
  const currentEvent = useRef<any | null>(null);
  const [reScheduleBlock, setReScheduleBlock] = useState<boolean>(false);
  const [timelineEventToUnplan, setTimelineEventToUnplan] = useState<MyEvent | null>(null);
  const [selectedCategoryForBulk, setSelectedCategoryForBulk] = useState<any | null>(null);
  const [showCanceledModal, setShowCanceledModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainer = useRef(false);

  const actionsContainerRef = useRef<HTMLDivElement | null>(null);

  const isDailyPlanPage = useIsDailyPlanPage();

  const { PLANNER_VARIANTS, CAPTURE_LIST_VARIANTS } = getPlannerMotionVariants(isDailyPlanPage);

  const isCalendarExpanded = useToggleExpandCalendar((state) => state.isCalendarExpanded);

  const planListSegmentedOptions = useMemo(
    () => [
      {
        value: ALL_ACTIONS,
        label: t('segmentedButtons.allActions'),
      },
      {
        value: UNPLANNED_ACTIONS,
        label: t('segmentedButtons.unplanned'),
      },
    ],
    [t],
  );

  const loadActionsPeriod = useMemo(
    () => [
      {
        value: 0,
        label: t('loadPeriods.thisWeekOnly'),
      },
      {
        value: 1,
        label: t('loadPeriods.includePastWeeks'),
      },
    ],
    [t],
  );

  const {
    categories,
    notEmptyCategories,
    formattedBlocks,
    filterWeeklyUnplannedActions,
    filterDailyUnscheduledActions,
    filterDailyScheduledActions,
  } = useCaptureListData(selectedSegmented);

  const {
    isOpen: isOpenCreateBlockModal,
    onOpen: openCreateBlockModal,
    onClose: onCloseCreateBlockModal,
  } = useDisclosure();

  const { isOpen: isOpenEditBlockModal, onOpen: openEditBlockModal, onClose: onCloseEditBlockModal } = useDisclosure();
  const {
    isOpen: isOpenDuplicateBlockModal,
    onOpen: openDuplicatedBlockModal,
    onClose: onCloseDuplicatedBlockModal,
  } = useDisclosure();

  const {
    isOpen: isOpenSetUncompletedActionsModal,
    onOpen: openSetUncompletedActionsModal,
    onClose: onCloseSetUncompletedActionsModal,
  } = useDisclosure();

  const {
    isOpen: isOpenDeleteBlockModal,
    onOpen: openDeleteBlockModal,
    onClose: onCloseDeleteBlockModal,
  } = useDisclosure();

  const {
    isOpen: isOpenConfirmDeleteBlockModal,
    onOpen: openConfirmDeleteBlockModal,
    onClose: onCloseConfirmDeleteBlockModal,
  } = useDisclosure();

  const {
    isOpen: isScheduleEventModalOpen,
    onOpen: onOpenScheduleEventModal,
    onClose: onCloseScheduleEventModal,
  } = useDisclosure();

  const {
    isOpen: isOpenRecurringInBlockModal,
    onOpen: openRecurringInBlockModal,
    onClose: closeRecurringInBlockModal,
  } = useDisclosure();

  const [isEditRecurringEvent, setIsEditRecurringEvent] = useBoolean();
  const [isDeleteRecurringEvents, setIsDeleteRecurringEvents] = useBoolean();

  const { data: categoriesData } = useCategories();

  const { onGetCurrentActionData: getCurrentActionData, setActionModalOpen } = useActionModal();

  const selectedDate = useCalendarMonthlyStore((state) => state.selectedDate);
  // Stable identities: these feed useMemo/useCallback deps below (and child props), so
  // rebuilding them inline would recompute the expensive transforms on every render.
  const { startDate, endDate, weekDays } = useMemo(() => {
    const start = selectedDate && startOfTheWeek(selectedDate);
    const end = selectedDate && endOfTheWeek(selectedDate);
    return { startDate: start, endDate: end, weekDays: getDaysWithinDatesRange(start, end) };
  }, [selectedDate]);

  const recurringWeekContext = useMemo(
    () =>
      !isDailyPlanPage && startDate && endDate ? { weekStart: startDate, weekEnd: endDate } : null,
    [isDailyPlanPage, startDate, endDate],
  );

  const captureDurationRange = useMemo(() => {
    if (!selectedDate) {
      return null;
    }
    if (isDailyPlanPage) {
      return { start: startOfDay(selectedDate), end: endOfDay(selectedDate) };
    }
    if (startDate && endDate) {
      return { start: startDate, end: endDate };
    }
    return null;
  }, [selectedDate, isDailyPlanPage, startDate, endDate]);

  const actionsPeriod = useCalendarMonthlyStore((state) => state.actionsPeriod);
  const updateActionsPeriod = useCalendarMonthlyStore((state) => state.updateActionsPeriod);

  const { data: weeklyPlanId } = useWeeklyPlanId(selectedDate);

  const { data: cronofyProfiles } = useCronofyProfiles();

  const { data, isLoading } = useActionsBlocksAndEvents(undefined);

  const { data: externalEvents } = useExternalEvents(startDate, endDate);

  const items = useMemo<ItemType>(() => {
    let captureList: ActionEventType[] = [];
    if (!data) {
      return {
        captureList,
      };
    }

    const activeActions = data.action.filter((a) => !a.isCanceled);
    if (selectedSegmented === UNSCHEDULED_ACTIONS) {
      captureList = isDailyPlanPage
        ? filterDailyUnscheduledActions(formatActions(activeActions))
        : filterWeeklyUnplannedActions(formatActions(activeActions));
    } else {
      captureList = isDailyPlanPage
        ? filterDailyScheduledActions(formatActions(activeActions))
        : formatActions(activeActions);
    }

    const categories = categoriesData?.category?.map((category) => {
      const actionsEvents = getActionsByCategoryId(data.action, category.id)
        .filter((a) => !a.isCanceled)
        .filter(isPlannedAction);

      const events: CategoryActionType[] = actionsEvents.flatMap((action) => {
        const recurrence = action.event?.recurrence;

        // Treat anything without a usable, known frequency (missing/empty rules, '' or an
        // unrecognized frequency) as non-recurring — otherwise the loop below never advances and
        // hangs the browser. getIncrementInterval returns null for all of those cases.
        if (!recurrence || !getIncrementInterval(recurrence.rules?.[0])) {
          return [
            {
              event: {
                id: String(action?.event?.id),
                action: { ...action, typeName: 'Action' },
                actionId: action.id,
                typeName: 'Event',
              },
            },
          ];
        }

        const fullEndDate = recurrence?.rules?.[0]?.until;
        const fullDate = action.event.start?.split('T')[0];

        const frequency = recurrence.rules?.[0]?.frequency;

        const start = utcToLocalDate(
          fullDate || action.scheduledDate,
          action.scheduledTime,
          action.timezone,
          action.gmtOffset,
        );
        const until = fullEndDate
          ? utcToLocalDate(fullEndDate, action.scheduledTime, action.timezone, action.gmtOffset)
          : endDate;

        const exceptions = getExceptionDates(recurrence, action);

        const customs = recurrence.exceptions?.custom;

        const instances: CategoryActionType[] = [];
        let current = start;
        const validDays = recurrence.rules?.[0]?.by_day;
        const interval = recurrence.rules?.[0]?.interval || 1;
        const isWeeklyRecurrence = frequency === 'weekly';
        const maxOccurrences = recurrence?.rules?.[0]?.count;
        let occurrenceCount = 0;
        let safety = 0;

        // Only occurrences inside the selected week survive groupEventsByWeekDay below, so
        // long-running series skip straight to it. Capped rules (count) must enumerate from
        // the start, and a truthy-but-EMPTY by_day must not clamp: this loop's `validDays ?`
        // truthiness advance walks (and emits) daily for that shape, unlike the clamp's
        // plain-weekly jump.
        if (startDate && !maxOccurrences && !(validDays && validDays.length === 0)) {
          current = fastForwardRecurrenceCursor(current, startDate, frequency, interval, validDays);
        }

        while (
          (!until || isBefore(current, until) || isEqual(current, until)) &&
          (!maxOccurrences || occurrenceCount < maxOccurrences) &&
          safety++ < 10000 // safety cap: never loop forever on malformed recurrence data
        ) {
          let formattedCurrent = formatDateToString(current);

          // Apply timezone conversion if the action has scheduledTime
          if (action.scheduledTime) {
            const { scheduledDate } = localDateToUTC(current);
            formattedCurrent = scheduledDate;
          }

          const progressStatus =
            customs?.[formattedCurrent]?.progressStatus === COMPLETE ||
            customs?.[formattedCurrent]?.progressStatus === INCOMPLETE ||
            customs?.[formattedCurrent]?.progressStatus === IN_PROGRESS
              ? customs?.[formattedCurrent]?.progressStatus
              : action?.progressStatus;

          const dateOfStarring = customs?.[formattedCurrent]?.dateOfStarring
            ? customs?.[formattedCurrent]
            : action?.dateOfStarring;

          const isException = isExceptionDate(current, exceptions);
          const isValidWeekly = validDays?.length && !isValidWeeklyDay(current, validDays);

          const isValidRecurrence =
            frequency === 'daily' ||
            (!isValidWeekly && isWeeklyRecurrence) ||
            frequency === 'monthly' ||
            frequency === 'yearly';

          if (!isException && isValidRecurrence) {
            instances.push({
              event: {
                id: String(action.event?.id) + '__' + formattedCurrent,
                action: {
                  ...action,
                  typeName: 'Action',
                  scheduledDate: formattedCurrent,
                  progressStatus: progressStatus as ProgressStatus,
                  dateOfStarring: dateOfStarring as Date,
                },
                actionId: action.id + formattedCurrent,
                typeName: 'Event',
              },
            });

            occurrenceCount++;
          }

          if (validDays?.length && shouldIncrementByWeek(current, validDays) && isWeeklyRecurrence && interval > 1) {
            const daysFromMonday = (current.getDay() + 6) % 7;
            current = add(current, { days: -daysFromMonday + interval * 7 });
            continue;
          } else {
            if (frequency === 'daily') {
              current = addDays(current, interval);
            } else if (isWeeklyRecurrence) {
              current = validDays ? addDays(current, 1) : addWeeks(current, interval);
            } else if (frequency === 'monthly') {
              current = addMonths(current, interval);
            } else if (frequency === 'yearly') {
              current = addYears(current, interval);
            }
          }
        }

        return instances;
      });

      return {
        ...category,
        actions: events,
      };
    });

    return {
      ...groupEventsByWeekDay(categories ?? [], weekDays),
      ...groupActionsByBlockId(data.block, data.action),
      captureList,
    };
  }, [
    data,
    selectedSegmented,
    categoriesData?.category,
    weekDays,
    startDate,
    endDate,
    filterDailyUnscheduledActions,
    filterWeeklyUnplannedActions,
    filterDailyScheduledActions,
    isDailyPlanPage,
  ]);

  const updateActionMutation = useUpdateAction();

  const addActionToBlock = useAddActionToBlock();

  const removeActionFromBlock = useRemoveActionFromBlock();

  const updateActionsCategoryOrder = useUpdateActionsCategoryOrder();

  const updateBlockActionsBlockOrder = useUpdateBlockActionsBlockOrder();

  const createEvent = useCreateEvent();

  const createEventOne = useEvent();

  const deleteEventAndUpdateAction = useDeleteEventAndUpdateAction({
    onSuccess: () => {
      setIsDeleteRecurringEvents.off();
      currentEvent.current = null;
    },
  });

  const deleteEventAndUpdateBlockAction = useDeleteEventAndUpdateBlockAction();

  const deleteBlock = useDeleteBlock();

  const updateEvent = useUpdateEventByPk({
    onSuccess: () => {
      setIsDeleteRecurringEvents.off();
      setIsEditRecurringEvent.off();
      setNewTimeChanged(undefined);
      currentEvent.current = null;
    },
  });

  const completeBlockStatus = useCompleteBlockStatus({
    onMutate: ({ id }) => onMutateBlockStatus(id, true),
  });

  const createAction = useCreateAction();

  const incompleteBlockStatus = useIncompleteBlockStatus({
    onMutate: ({ id }) => onMutateBlockStatus(id),
  });

  const handleCloseDeleteDuplicatedAction = useCallback(() => {
    setEventToUnplan(null);
    setIsDeleteRecurringEvents.off();
    setIsEditRecurringEvent.off();
  }, [setIsDeleteRecurringEvents]);

  const handleCloseEditRecurringAction = useCallback(() => {
    setEventToUnplan(null);
    setIsEditRecurringEvent.off();
    setNewTimeChanged(undefined);
  }, [setIsEditRecurringEvent]);

  /* <Dnd> */
  const collisionDetectionStrategy = useCallback<CollisionDetection>(
    (args) => {
      if (activeId && activeId in items) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((container) => container.id in items),
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      const intersections =
        pointerIntersections.length > 0
          ? // If there are droppables intersecting with the pointer, return those
            pointerIntersections
          : rectIntersection(args);

      let overId = getFirstCollision(intersections, 'id');

      // We have to ensure that overId is not null or undefined
      if (overId != null) {
        if (overId in items && !overId.toString().startsWith('event')) {
          const containerItems = items[overId];

          // If a container is matched and it contains items (columns 'A', 'B', 'C')
          if (containerItems.length > 0) {
            // Return the closest droppable within that container
            const tempOverId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) =>
                  container.id !== overId && containerItems.find((item) => item.action.id === container.id),
              ),
            })[0]?.id;

            if (!isUndefined(tempOverId)) {
              overId = tempOverId;
            }
          }
        }

        lastOverId.current = overId;

        return [{ id: overId }];
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeId, items],
  );

  const onDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id);
    setActiveItemCameFrom(active?.data?.current?.containerId);
  };

  const updateEventDate = useCallback(
    (actionId: string, scheduledDate: string, scheduledTime: string | null) => {
      updateActionMutation.mutate(
        {
          actionId: actionId,
          set: {
            scheduledDate,
            scheduledTime,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            gmtOffset: getGMTOffset(Intl.DateTimeFormat().resolvedOptions().timeZone),
          },
        },
        {
          onSuccess: (actionData) => {
            const recurrence = actionData.updateActionByPk.event?.recurrence;
            if (recurrence && Object.keys(recurrence).length) {
              const eventId = actionData.updateActionByPk.event?.id;
              const newStart = actionData?.updateActionByPk?.scheduledDate || '';
              updateEvent.mutate({
                id: eventId as string,
                recurrence: { ...recurrence },
                start: newStart,
              });
            }
          },
        },
      );
    },
    [updateActionMutation],
  );

  const createEventIfNotExistsAndUpdateAction = useCallback(
    (actionId: string, scheduledDate: string, scheduledTime: string | null = null, timeZone?: string) => {
      if (!data?.action) {
        return;
      }

      if (!getEventByActionId(data.action, actionId)) {
        createEvent.mutate({
          id: uuidv4(),
          actionId,
          scheduledDate,
          scheduledTime,
          timezone: timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
          gmtOffset: getGMTOffset(timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone),
        });

        return;
      }

      const event = getEventByActionId(data.action, actionId);

      if (!event) {
        return;
      }

      updateEventDate(actionId, scheduledDate, scheduledTime ?? event.action.scheduledTime);
    },
    [data?.action, updateEventDate, createEvent],
  );

  const moveActions = (reorderItems: ActionEventType[]) => {
    return reorderItems.map((action, index) => ({
      ...action,
      action: {
        ...action.action,
        categoryOrder: index + 1,
      },
    }));
  };

  const updateCaptureListActionsOrder = useCallback(
    (actions: ActionEventType[]) => {
      const mutationPayload = actions.map((action, index) => ({
        _set: {
          categoryOrder: index + 1,
        },
        where: {
          id: {
            _eq: action.action.id,
          },
        },
      }));

      updateActionsCategoryOrder.mutate(mutationPayload);
    },
    [updateActionsCategoryOrder],
  );

  const updateBlockActionsOrder = useCallback(
    (actions: Action[]) => {
      const mutationPayload = actions.map((action, index) => ({
        _set: {
          blockOrder: index + 1,
        },
        where: {
          id: {
            _eq: action.id,
          },
        },
      }));

      updateBlockActionsBlockOrder.mutate({
        updates: mutationPayload,
        categoryId: actions[0].categoryId,
        projectId: actions[0].projectId,
      });
    },
    [updateBlockActionsBlockOrder],
  );

  const handleDeleteEventAndUpdateAction = useCallback(
    async (eventId: string, actions: ActionEventType[], blockId: string | null) => {
      const event = getEventById(data.action, eventId);

      if (event == null) {
        return;
      }

      if (!event.action.scheduledDate) {
        return;
      }

      let shouldDeleteBlock = false;

      if (blockId) {
        const block = data.block.find((block) => block.id === blockId);
        const blockActions = block?.actions;

        const blockUpdatedAction = blockActions?.find((action) => action.id === event.actionId);

        if (blockUpdatedAction != null) {
          actions.push(createActionEvent({ ...blockUpdatedAction, typeName: 'Action' }));
        }

        if (blockActions?.length === 1) {
          shouldDeleteBlock = true;
        }
      }

      let categoryOrder = 0;

      if (data.action.length) {
        categoryOrder = data.action.length;
      }

      await deleteEventAndUpdateAction.mutateAsync({
        eventId,
        actionId: event.actionId,
        scheduledDate: isDailyPlanPage
          ? formatDateToString(
              utcToLocalDate(
                event.action.scheduledDate,
                event.action.scheduledTime,
                event.action.timezone,
                event.action.gmtOffset,
              ),
            )
          : null,
        categoryOrder,
      });

      if (shouldDeleteBlock && blockId) {
        deleteBlock.mutate({
          blockId,
        });
      }
    },
    [data.action, data.block, deleteEventAndUpdateAction, deleteBlock, isDailyPlanPage],
  );

  const onCloseAndResetCreateBlockModal = useCallback(() => {
    setTempBlockActionId(null);
    setTempCategoryId(null);
    setTempProjectId(null);
    onCloseCreateBlockModal();
  }, [onCloseCreateBlockModal]);

  const handleAddActionToBlock = useCallback(
    (actionId: string, block: Block) => {
      if (!data?.block) {
        return;
      }

      const draggedAction = getActionById(data.action, actionId);
      if (actionHasRecurrenceForbiddenInBlock(draggedAction)) {
        openRecurringInBlockModal();
        return;
      }

      addActionToBlock.mutate({
        actionId,
        blockId: block.id,
        blockOrder: data?.block?.length ?? 0,
        projectId: block.projectId,
        categoryId: block.categoryId,
      });

      incompleteBlockStatus.mutate({ id: block.id });

      const cacheKeys = [];
      cacheKeys.push(keys.blocks.all.queryKey);
      block.projectId && cacheKeys.push(keys.project.detail(block.projectId).queryKey);
      invalidateQueries(cacheKeys);

      onCloseAndResetCreateBlockModal();
    },
    [data.block, data.action, addActionToBlock, incompleteBlockStatus, onCloseAndResetCreateBlockModal, openRecurringInBlockModal],
  );

  const moveActionFromBlockToCaptureList = useCallback(
    (actionId: string, blockId: string, shouldDeleteBlock: boolean) => {
      if (!data) {
        return;
      }

      const block = getBlockById(data.block, blockId);

      if (shouldDeleteBlock && block) {
        setSelectedBlock(block);
        openConfirmDeleteBlockModal();
        return;
      }

      removeActionFromBlock.mutate({
        actionId,
        categoryId: block?.categoryId,
        projectId: block?.projectId,
      });
    },
    [data, openConfirmDeleteBlockModal, removeActionFromBlock],
  );

  const handleDeleteEventAndUpdateBlockAction = useCallback(
    (_actions: ActionEventType[], eventId: string, blockId: string) => {
      const event = getEventById(data.action, eventId);

      if (event == null) {
        return;
      }

      const actionForEvent = getActionById(data.action, event.actionId);
      if (actionHasRecurrenceForbiddenInBlock(actionForEvent)) {
        openRecurringInBlockModal();
        return;
      }

      deleteEventAndUpdateBlockAction.mutate({
        eventId,
        actionId: event.actionId,
        scheduledDate: isDailyPlanPage ? event.action.scheduledDate : null,
        blockId,
        blockOrder: event.action.blockOrder,
      });
    },
    [data.action, deleteEventAndUpdateBlockAction, isDailyPlanPage, openRecurringInBlockModal],
  );

  // !TODO i think this can be removed and is a duplicate from the dailyPlan page but
  // it needs to be investigated to be sure
  const handleDailyActionDropped = useCallback(
    async (dateTime: ScheduleEventDateTime) => {
      if (!activeId) {
        return;
      }

      // Dropping an action into daily view. Set the action info and create event
      const actionId = String(activeId);

      const { scheduledDate, scheduledTime, timeZone } = dateTime;

      const action = getActionById(data.action, actionId);

      if (!action) {
        return;
      }

      if (!action.scheduledDate) {
        await updateActionMutation.mutateAsync({
          actionId,
          set: {
            scheduledDate,
            scheduledTime,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            gmtOffset: getGMTOffset(Intl.DateTimeFormat().resolvedOptions().timeZone),
          },
        });
      }

      // Check if the action already has an associated event.
      createEventIfNotExistsAndUpdateAction(actionId, scheduledDate, scheduledTime, timeZone);
      trackActionScheduledByDrag({ ...action, scheduledDate });
      onCloseScheduleEventModal();
    },
    [activeId, createEventIfNotExistsAndUpdateAction, data.action, onCloseScheduleEventModal, updateActionMutation],
  );

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      const activeContainer = findContainerById(active.id, items);

      if (!activeContainer || active.id == null) {
        setActiveId(null);
        setActiveItemCameFrom('');
        return;
      }

      let overId = over?.id;
      if (over?.data?.current?.containerId === 'blocks') {
        // special case when an item is dropped over a block but not over the list of actions
        overId = `block-${overId}`;
      }

      if (overId == null) {
        setActiveId(null);
        setActiveItemCameFrom('');
        return;
      }

      if (overId === 'timeline') {
        setActiveId(active.id);
        onOpenScheduleEventModal();
        return;
      }

      const overContainer = findContainerById(overId, items);

      if (overContainer) {
        let activeIndex = items[activeContainer].findIndex(({ id }) => id === active?.id);
        let overIndex = items[overContainer].findIndex(({ id }) => id === over?.id);

        const overContainerId = over?.data?.current?.containerId;

        if (
          active?.data?.current?.category?.id !== over?.data?.current?.category?.id &&
          over?.data?.current?.category?.id !== 'captureListContainer'
        ) {
          return;
        }

        const activeProjectId = active?.data?.current?.projectId;
        const overProjectId = over?.data?.current?.projectId;

        if (
          overContainerId.toString().startsWith('block-') &&
          ![null, undefined].includes(activeProjectId, overProjectId) &&
          activeProjectId !== overProjectId
        ) {
          return;
        }

        if (activeItemCameFrom === 'captureList' && overContainerId.startsWith('event')) {
          createEventIfNotExistsAndUpdateAction(active.id.toString(), over?.data?.current?.day);
          const action = getActionById(data.action, active.id.toString());
          action && trackActionPlannedByDrag({ ...action, scheduledDate: over?.data?.current?.day });
        }

        if (activeItemCameFrom.startsWith('event') && overContainerId.startsWith('event')) {
          const parentId = active.id.toString().split('__')[0];
          const event = getEventById(data.action, parentId ?? active.id.toString());

          if (!event) {
            return;
          }

          if (over?.data?.current?.day) {
            if (validateRecurrence(event.action.event?.recurrence)) {
              currentEvent.current = event;
              setIsEditRecurringEvent.on();
              const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
              const originalScheduledTime = event.action.scheduledTime;
              setNewTimeChanged({
                scheduledDate: over.data.current.day,
                scheduledTime: originalScheduledTime,
                gmtOffset: getGMTOffset(timezone),
                timeZone: timezone,
              });
              if (active.data.current) currentEvent.current.activeDay = active.data.current.day;
            } else {
              updateEventDate(event.actionId, over.data.current.day, null);
            }
          }
        }

        if (activeItemCameFrom === 'captureList' && overContainerId === 'captureList') {
          updateCaptureListActionsOrder(moveActions(arrayMove(items[overContainer], activeIndex, overIndex)));
        }

        if (activeItemCameFrom.startsWith('event') && overContainerId === 'captureList') {
          let blockId = getEventById(data.action, active.id.toString())?.action?.blockId ?? null;

          let actions: ActionEventType[] = [];

          if (items[overContainer].length) {
            actions = moveActions(arrayMove(items[overContainer], activeIndex, overIndex));
          }

          handleDeleteEventAndUpdateAction(active.id.toString(), actions, blockId);
        }

        if (activeItemCameFrom.startsWith('block') && overContainerId.startsWith('event')) {
          createEventIfNotExistsAndUpdateAction(active.id.toString(), over?.data?.current?.day);
          const action = getActionById(data.action, active.id.toString());
          action && trackActionPlannedByDrag({ ...action, scheduledDate: over?.data?.current?.day });
        }

        if (activeItemCameFrom === 'captureList' && overContainerId.toString().startsWith('block')) {
          const block = getBlockById(data?.block ?? [], over?.data?.current?.blockId);

          if (block) {
            handleAddActionToBlock(active.id.toString(), block);
          }
        }

        if (activeItemCameFrom.startsWith('block') && overContainerId === 'captureList') {
          moveActionFromBlockToCaptureList(
            active.id.toString(),
            active?.data?.current?.blockId,
            items[activeContainer].length === 1,
          );
        }

        if (
          activeContainer.toString().startsWith('block') &&
          overContainerId.startsWith('block') &&
          activeContainer.toString() !== overContainerId
        ) {
          const block = getBlockById(data?.block ?? [], over?.data?.current?.blockId);

          if (block) {
            handleAddActionToBlock(active.id.toString(), block);
          }
        }

        if (
          activeContainer.toString().startsWith('block') &&
          overContainerId.startsWith('block') &&
          activeContainer.toString() === overContainerId
        ) {
          const blockIncompleteActions = orderBy(
            getIncompleteActionsByBlockId(active?.data?.current?.blockId, data.action),
            'blockOrder',
            'asc',
          );

          activeIndex = blockIncompleteActions.findIndex(({ id }) => id === active?.id);
          overIndex = blockIncompleteActions.findIndex(({ id }) => id === over?.id);

          updateBlockActionsOrder(arrayMove(blockIncompleteActions, activeIndex, overIndex));
        }

        if (activeItemCameFrom.startsWith('event') && overContainerId.startsWith('block')) {
          let actions: ActionEventType[] = [];

          if (items[overContainer].length) {
            actions = moveActions(arrayMove(items[overContainer], activeIndex, overIndex));
          }

          handleDeleteEventAndUpdateBlockAction(actions, active.id.toString(), over?.data?.current?.blockId);
        }
      }

      setActiveId(null);
      setActiveItemCameFrom('');
    },
    [
      items,
      onOpenScheduleEventModal,
      activeItemCameFrom,
      createEventIfNotExistsAndUpdateAction,
      data.action,
      data?.block,
      updateEventDate,
      updateCaptureListActionsOrder,
      handleDeleteEventAndUpdateAction,
      handleAddActionToBlock,
      moveActionFromBlockToCaptureList,
      updateBlockActionsOrder,
      handleDeleteEventAndUpdateBlockAction,
    ],
  );

  const handleChangeEventTime = useCallback(
    (time: Date, event: ActionEventType) => {
      const { scheduledDate, scheduledTime } = localDateToUTC(time);

      updateActionMutation.mutate({
        actionId: event.actionId,
        set: {
          scheduledDate,
          scheduledTime,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          gmtOffset: getGMTOffset(Intl.DateTimeFormat().resolvedOptions().timeZone),
        },
      });
    },
    [updateActionMutation],
  );

  const handleRemoveActionFromBlock = useCallback(
    (actionId: string, blockId: string) => {
      const key = `block-${blockId}`;

      moveActionFromBlockToCaptureList(actionId, blockId, items[key].length === 1);
    },
    [items, moveActionFromBlockToCaptureList],
  );

  const openActionModal = useCallback(
    (isEditingEventAction = false) => {
      setActionModalOpen(true, {
        onCreateAction: () => null,
        readonlyCategory: isEditingEventAction,
        readonlyTooltipLabel: isEditingEventAction ? t('tooltips.readonlyTooltipLabel') : undefined,
      });
    },
    [setActionModalOpen],
  );

  const onClickEvent = useCallback(
    (action: Action) => {
      if (action?.blockId) {
        const block = getBlockById(formattedBlocks, action.blockId);

        action.block = block ?? null;
        action.isLastBlockAction = block?.actions?.length === 1;
      }

      getCurrentActionData(action);
      openActionModal(true);
    },
    [formattedBlocks, getCurrentActionData, openActionModal],
  );

  const onClickTimelineEvent = useCallback(
    (event: MyEvent) => {
      const action = event.__metadata?.action;
      if (!action) return;
      onClickEvent(action);
    },
    [onClickEvent],
  );

  const onDragCancel = () => {
    setActiveId(null);
    setActiveItemCameFrom('');
  };

  const ItemOverlay = useMemo(() => {
    if (!activeId) {
      return <></>;
    }

    const item = getItemById(activeId, items);

    if (!item) {
      return <></>;
    }

    const index = getIdx(activeId, items);

    return createPortal(
      <DragOverlay dropAnimation={dropAnimation}>
        {item?.typeName?.toLowerCase() === 'event' ? (
          <WeeklyEventItem event={item} onUnplan={() => null} onClickEvent={() => null} />
        ) : (
          <ActionRow
            action={item.action}
            idx={index + 1}
              recurringWeekContext={recurringWeekContext}
              disableRecurringStatusChanges
          />
        )}
      </DragOverlay>,
      document.body,
    );
  }, [activeId, items, recurringWeekContext]);

  /* </Dnd> */

  const onUpdateBlock = useCallback(() => {
    refetchBlocks();

    onCloseEditBlockModal();
    setSelectedBlock(null);
  }, [onCloseEditBlockModal]);

  const handleDeleteBlock = useCallback(
    (block: Block | undefined) => {
      if (!block) {
        return;
      }

      setSelectedBlock(block);
      openDeleteBlockModal();
    },
    [openDeleteBlockModal],
  );

  const onCompleteBlockActions = useCallback(() => {
    if (!selectedBlock) {
      return;
    }

    if (selectedBlock.actions) {
      const incompleteActions = getIncompleteActions(selectedBlock.actions);
      incompleteActions.forEach((action) => trackBulkActionCompleted(action));
    }

    completeActionsOfBlock(selectedBlock.categoryId);
    setSelectedBlock(null);
  }, [selectedBlock]);

  const onCompleteBlock = useCallback(
    (blockId: string) => {
      completeBlockStatus.mutate({
        id: blockId,
      });
    },
    [completeBlockStatus],
  );

  const onRemoveBlockActions = useCallback(() => {
    if (!selectedBlock) {
      return;
    }

    removeIncompleteActionsFromBlock(selectedBlock);
    onCompleteBlock(selectedBlock.id);

    setSelectedBlock(null);
  }, [onCompleteBlock, selectedBlock]);

  const onUncompleteBlock = useCallback(
    (blockId: string) => {
      if (!data) {
        return;
      }

      const block = getBlockById(data.block, blockId);

      if (!block) {
        return;
      }

      incompleteBlockStatus.mutate({
        id: blockId,
      });
    },
    [data, incompleteBlockStatus],
  );

  const handleRemoveEventTime = useCallback(
    (dateWithoutTime: string, event: ActionEventType) => {
      updateActionMutation.mutate({
        actionId: event.actionId,
        set: {
          scheduledDate: dateWithoutTime,
          scheduledTime: null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          gmtOffset: getGMTOffset(Intl.DateTimeFormat().resolvedOptions().timeZone),
        },
      });
    },
    [updateActionMutation],
  );

  // makeEvents expands every recurring action; without the memo it ran on every render.
  // selectedDate is a dependency because makeEvents reads the selected week from the calendar store.
  const events = useMemo(
    () =>
      makeEvents(
        categoriesData?.category ?? [],
        data.action.filter((a) => !a.isCanceled),
        externalEvents ?? [],
        cronofyProfiles,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedDate is read by makeEvents via the calendar store
    [categoriesData?.category, data.action, externalEvents, cronofyProfiles, selectedDate],
  );

  const createOrUpdateAction = async (
    action: Action,
    newTimeChanged: ActionTimeChangeProps,
    frequency?: RecurrenceRule,
  ) => {
    const actionData = {
      blockId: action?.blockId ?? undefined,
      categoryId: action?.categoryId ?? undefined,
      dateOfStarring: action?.dateOfStarring,
      duration: action?.duration ?? '',
      id: uuidv4(),
      isLocked: false,
      notes: action?.notes,
      progressStatus: INCOMPLETE as ProgressStatus,
      projectId: action?.projectId ?? null,
      scheduledDate: newTimeChanged?.scheduledDate,
      scheduledTime: newTimeChanged?.scheduledTime,
      timezone: newTimeChanged?.timeZone,
      title: action?.title,
      weeklyPlanId: weeklyPlanId,
      gmtOffset: newTimeChanged?.gmtOffset,
    };

    createAction.mutate(actionData, {
      onSuccess: async (data: any) => {
        const { id: actionId } = data.insertActionOne;

        if (data.insertActionOne.scheduledDate) {
          const eventData: any = {
            id: uuidv4(),
            actionId,
          };

          if (frequency && validateRecurrence({ rules: [frequency], exceptions: {} })) {
            const normalizedRule = normalizeRecurrenceRuleUntil(data.insertActionOne.scheduledDate, frequency, {
              timeZone:
                action?.timezone ?? newTimeChanged?.timeZone ?? (data.insertActionOne.timezone as string | undefined),
              gmtOffset:
                action?.gmtOffset ??
                newTimeChanged?.gmtOffset ??
                (data.insertActionOne.gmtOffset as string | undefined),
            });

            eventData.recurrence = {
              rules: [normalizedRule],
              exceptions: {},
            };
            eventData.start = data.insertActionOne.scheduledDate;
            eventData.end = null;
          }

          const event = await createEventOne.mutateAsync(eventData);

          if (event?.insertEventOne) {
            data.insertActionOne.event = {
              id: event.insertEventOne.id,
              typeName: 'Event',
            };
          }
        }

        refetchActions(data.insertActionOne, weeklyPlanId);
      },
    });
  };

  function hasPreviousEvent(data: Record<string, EventsPerDayType>, currentEventDate: Date) {
    return Object.values(data).some((entry) =>
      entry.events.some((event) =>
        isBefore(
          utcToLocalDate(
            event!.action!.event!.start!.split('T')[0],
            currentEvent.current?.action.scheduledTime,
            currentEvent.current?.action.timezone,
            currentEvent.current?.action.gmtOffset,
          ),
          currentEventDate,
        ),
      ),
    );
  }

  const isUniqueEvent = (data: Record<string, EventsPerDayType>, eventID: string, currentEventDate: Date): boolean => {
    const allOccurrences = Object.values(data)
      .flatMap((entry) => entry.events)
      .filter((event) => event?.action?.event?.id === eventID);

    if (allOccurrences.length > 1) return false;

    const hasPrevious = allOccurrences.some((event) =>
      isBefore(
        utcToLocalDate(
          event!.action!.event!.start!.split('T')[0],
          currentEvent.current?.action.scheduledTime,
          currentEvent.current?.action.timezone,
          currentEvent.current?.action.gmtOffset,
        ),
        currentEventDate,
      ),
    );

    const hasNext = allOccurrences.some((event) =>
      isAfter(
        utcToLocalDate(
          event!.action!.event!.start!.split('T')[0],
          currentEvent.current?.action.scheduledTime,
          currentEvent.current?.action.timezone,
          currentEvent.current?.action.gmtOffset,
        ),
        currentEventDate,
      ),
    );

    return !hasPrevious && !hasNext;
  };

  const deleteRecurringEvents = (option: EventOptions | null) => {
    const action = currentEvent.current?.action;

    const currentExceptions = action?.event?.recurrence?.exceptions?.add || [];

    const recurrenceRule = action?.event?.recurrence?.rules?.[0] || '';
    const untilEvent = getValueInRecurrenceRule(action?.event.recurrence, 'until') || '';

    const localDate = utcToLocalDate(action.scheduledDate, action.scheduledTime, action.timezone, action.gmtOffset);

    if (option === EventOptions.THIS_EVENT) {
      const isUnique = isUniqueEvent(events.data[0].events, action.event.id, localDate);
      if (isUnique) {
        if (action.blockId) {
          handleDeleteEventAndUpdateBlockAction(items[action.blockId], action.event.id, action.blockId);
          return;
        }
        handleDeleteEventAndUpdateAction(action.event.id, items.captureList, null);
      } else {
        updateEvent.mutate({
          id: action?.event?.id as string,
          recurrence: {
            rules: [
              {
                ...recurrenceRule,
                ...(untilEvent ? { until: untilEvent } : {}),
              },
            ],
            exceptions: {
              add: [...currentExceptions, { date: action?.scheduledDate }],
            },
          },
          start: action?.event?.start,
        });
      }
    }

    if (option === EventOptions.ALL_EVENTS) {
      if (!action?.event?.id) return;

      if (action.blockId) {
        handleDeleteEventAndUpdateBlockAction(items[action.blockId], action.event.id, action.blockId);
        return;
      }

      handleDeleteEventAndUpdateAction(action.event.id, items.captureList, null);
    }

    if (option === EventOptions.THIS_AND_FOLLOWING_EVENTS) {
      const isNotFirstEvent = hasPreviousEvent(events.data[0].events, localDate);
      const eventEnd = currentEvent.current?.action?.scheduledDate;

      if (isNotFirstEvent) {
        updateEvent.mutate({
          id: action?.event?.id as string,
          recurrence: {
            rules: [
              {
                ...recurrenceRule,
                until: eventEnd,
              },
            ],
            exceptions: {
              add: [...currentExceptions, { date: eventEnd }],
            },
          },
          start: action?.event?.start,
          end: eventEnd,
        });
      } else {
        if (action.blockId) {
          handleDeleteEventAndUpdateBlockAction(items[action.blockId], action.event.id, action.blockId);
          return;
        }

        handleDeleteEventAndUpdateAction(action.event.id, items.captureList, null);
      }
    }
  };

  const editRecurringEvents = (option: EventOptions | null) => {
    const action = currentEvent.current?.action;
    const currentExceptions = action?.event?.recurrence?.exceptions?.add || [];
    const recurrenceRules = action?.event?.recurrence?.rules?.[0] as RecurrenceRule;

    switch (option) {
      case EventOptions.THIS_EVENT: {
        if (!action) return;

        if (!newTimeChanged) return;

        // For THIS_EVENT, always create exception and new action regardless of position
        const newDate = currentEvent.current.activeDay;
        const dateExists = currentExceptions?.some((e: any) => e.date === newDate);

        const exceptions = dateExists ? currentExceptions : [...currentExceptions, { date: newDate }];

        updateEvent.mutate({
          id: action?.event?.id as string,
          recurrence: {
            rules: [recurrenceRules],
            exceptions: {
              add: exceptions,
            },
          },
          start: action.event?.start,
        });

        createOrUpdateAction(action, newTimeChanged);

        break;
      }
      case EventOptions.THIS_AND_FOLLOWING_EVENTS: {
        if (!action) return;

        if (!newTimeChanged) return;

        const originalStartDate = action.event?.start?.split('T')[0];
        const movingToDate = newTimeChanged.scheduledDate;
        const currentActionDate = currentEvent.current.activeDay;

        // Check if this is the first instance being moved
        const isFirstInstance = originalStartDate === currentActionDate;

        if (isFirstInstance) {
          // Update the original action's start date for the entire series
          const normalizedRule = normalizeRecurrenceRuleUntil(movingToDate, recurrenceRules, {
            timeZone: newTimeChanged?.timeZone ?? action?.timezone,
            gmtOffset: newTimeChanged?.gmtOffset ?? action?.gmtOffset,
            fallbackTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });

          updateEvent.mutate({
            id: action?.event?.id as string,
            recurrence: {
              rules: [normalizedRule],
              exceptions: { add: currentExceptions },
            },
            start: movingToDate,
          });

          // Also update the action's scheduled date
          updateActionMutation.mutate({
            actionId: action.id,
            set: {
              scheduledDate: movingToDate,
              scheduledTime: newTimeChanged.scheduledTime,
              timezone: newTimeChanged.timeZone,
              gmtOffset: newTimeChanged.gmtOffset,
            },
          });
        } else {
          const newDate = currentEvent.current.activeDay;

          const { untilUtc, exceptionUtc, exceptions } = computeRecurrenceExceptionDates({
            targetDate: newDate,
            scheduledTime: action.scheduledTime,
            timeZone: action.timezone,
            gmtOffset: action.gmtOffset,
            existingExceptions: currentExceptions,
            fallbackTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });

          updateEvent.mutate({
            id: action?.event?.id as string,
            recurrence: {
              rules: [
                {
                  ...recurrenceRules,
                  until: untilUtc,
                },
              ],
              exceptions: {
                add: exceptions,
              },
            },
            start: action.event?.start,
            end: exceptionUtc,
          });

          createOrUpdateAction(action, newTimeChanged, recurrenceRules);
        }

        break;
      }

      default:
        break;
    }
  };

  const unplanAction = useCallback(
    (event: any) => {
      const parentId = event.id.toString().split('__')[0];

      let eventData = getEventById(data.action, parentId);

      if (validateRecurrence(eventData?.action.event?.recurrence)) {
        setIsDeleteRecurringEvents.on();
        currentEvent.current = event;
        return;
      }

      if (!eventData) {
        event = getEventByActionId(data.action, event.id);
      }

      if (!eventData) {
        return;
      }

      if (eventData.action.blockId) {
        handleDeleteEventAndUpdateBlockAction(items[eventData.action.blockId], eventData.id, eventData.action.blockId);
        return;
      }

      handleDeleteEventAndUpdateAction(eventData.id, items.captureList, null);
    },
    [data.action, handleDeleteEventAndUpdateAction, handleDeleteEventAndUpdateBlockAction, items],
  );

  const unplanTimelineEvent = useCallback(
    (event: MyEvent) => {
      const action = event.__metadata?.action;
      if (!action?.event?.id) return;

      unplanAction(action.event.id);
      setTimelineEventToUnplan(null);
    },
    [unplanAction],
  );

  const onDeleteBlock = useCallback(() => {
    if (!selectedBlock) {
      return;
    }

    deleteBlock.mutate({ blockId: selectedBlock.id });

    onCloseDeleteBlockModal();
    onCloseConfirmDeleteBlockModal();
    setSelectedBlock(null);
    onCloseEditBlockModal();
  }, [deleteBlock, onCloseConfirmDeleteBlockModal, onCloseDeleteBlockModal, onCloseEditBlockModal, selectedBlock]);

  const onCreateBlockFromAction = useCallback(
    (actionId: string) => {
      setTempBlockActionId(actionId);
      const tempAction = getActionById(data.action, actionId);
      setTempCategoryId(tempAction?.categoryId ?? null);
      setTempProjectId(tempAction?.projectId ?? null);
      openCreateBlockModal();
    },
    [openCreateBlockModal, data.action],
  );

  const captureListAnimate = useMemo(() => {
    if (isCalendarExpanded) {
      return 'plannerExpanded';
    }

    return 'default';
  }, [isCalendarExpanded]);

  useEffect(() => {
    const cacheKey =
      actionsPeriod === LOAD_ACTIONS_PERIOD[1].value
        ? keys.actions.all._ctx.weekAndPast(weeklyPlanId)
        : keys.actions.all._ctx.week(weeklyPlanId ?? '');
    queryClient.refetchQueries(cacheKey);
  }, [actionsPeriod, weeklyPlanId]);

  useEffect(() => {
    return () => {
      // force "this week only" when navigating away from this page
      updateActionsPeriod(LOAD_ACTIONS_PERIOD[0].value);
    };
  }, [updateActionsPeriod]);

  // I tried to remove this effect, but without it the unplan of actions
  // sometimes doesn't work, because the getEventById used inside the unplan
  // function doesn't find the action provided. Was unable to find the cause in
  // the time I had available.
  // TODO: find a way to remove this effect
  useEffect(() => {
    if (timelineEventToUnplan === null) {
      return;
    }

    unplanTimelineEvent(timelineEventToUnplan);

    return () => setTimelineEventToUnplan(null);
  }, [timelineEventToUnplan, unplanTimelineEvent]);

  if (isLoading) {
    return (
      <Flex justifyContent="center" marginTop={rem(80)}>
        <Spinner size="xl" speed="0.65s" thickness={rem(4)} />
      </Flex>
    );
  }

  return (
    <>
      <DndContainer
        collisionDetection={collisionDetectionStrategy}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <Flex height={`calc(100vh - 9rem)`} transition="right 0.8s">
          <CategorySelector />
          <AnimatePresence initial={false}>
            <motion.div
              variants={CAPTURE_LIST_VARIANTS}
              animate={captureListAnimate}
              exit="normalWidth"
              transition={{ duration: 0.5 }}
              style={{ height: 'inherit' }}
            >
              <Flex
                className="capture-list-container"
                ref={actionsContainerRef}
                sx={{
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehavior: 'contain',
                }}
                flexDirection="column"
                overflowY="auto"
                height="inherit"
                marginRight={rem(8)}
                paddingRight={rem(16)}
              >
                <DroppableContainer
                  id="captureList"
                  payload={{
                    containerId: 'captureList',
                    category: { id: 'captureListContainer' },
                  }}
                  height="full"
                >
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="nowrap"
                    flexDirection="row"
                    marginBottom={rem(24)}
                  >
                    {!SHOW_PAST_ACTIONS_FLAG ? (
                      <Text as="h2" textStyle="largeBlack">
                        {t('captureList.title')}
                      </Text>
                    ) : (
                      <Menu>
                        <MenuButton as={Button} rightIcon={<Box as={IconChevronDown} />} size="sm" variant="secondary">
                          {loadActionsPeriod.find((i) => i.value === actionsPeriod)?.label ??
                            t('loadPeriods.thisWeekOnly')}
                        </MenuButton>
                        <MenuList overflowY="scroll" maxHeight={rem(240)} backgroundColor="background-primary">
                          {loadActionsPeriod.map((period) => (
                            <MenuItem
                              key={period.value}
                              fontSize="sm"
                              _hover={{ backgroundColor: 'background-secondary' }}
                              backgroundColor="transparent"
                              onClick={() => updateActionsPeriod(period.value)}
                            >
                              {period.label}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    )}
                    <DynamicSegmentedButton
                      onChange={setSelectedSegmented}
                      options={planListSegmentedOptions}
                      layoutId="capture-list-plan"
                    />
                  </Flex>
                  {notEmptyCategories.map((category) => (
                    <Fragment key={category.id}>
                      <CaptureList
                        className={`${category.id}-completed-actions`}
                        category={category}
                        totalDurationOfIncompleteActions={
                          captureDurationRange && startDate && endDate
                            ? getTotalDurationOfActionsInRange(
                                filterIncompleteActions(
                                  category.incompleteActions,
                                  startDate,
                                  'week',
                                  endDate,
                                ),
                                captureDurationRange.start,
                                captureDurationRange.end,
                              )
                            : getTotalDurationOfActions(
                                filterIncompleteActions(category.incompleteActions, startDate, 'week', endDate),
                              )
                        }
                        totalDurationOfPreferredIncompleteActions={
                          captureDurationRange && startDate && endDate
                            ? getTotalDurationOfPreferredActionsInRange(
                                filterIncompleteActions(
                                  category.incompleteActions,
                                  startDate,
                                  'week',
                                  endDate,
                                ),
                                captureDurationRange.start,
                                captureDurationRange.end,
                              )
                            : getTotalDurationOfPreferredActions(
                                filterIncompleteActions(category.incompleteActions, startDate, 'week', endDate),
                              )
                        }
                        marginBottom={rem(16)}
                        threeDotsButton={
                          !isEmpty(category.incompleteActions) && (
                            <ThreeDotsLaterButton
                              showDoItLater={false}
                              onCancel={() => {
                                setSelectedCategoryForBulk(category);
                                setShowCanceledModal(true);
                              }}
                              onDelete={() => {
                                setSelectedCategoryForBulk(category);
                                setShowDeleteModal(true);
                              }}
                            />
                          )
                        }
                        addEntityButton={
                          <AddActionButton
                            backgroundColor={category?.color}
                            withActionBlockCreationMenu={true}
                            onExternalClick={() => {
                              setActionModalOpen(true, { categoryId: category?.id, location: 'planner' });
                            }}
                            onCreateNewBlock={() => {
                              setTempCategoryId(category.id);
                              openCreateBlockModal();
                            }}
                            color="text-primary"
                            textColor="text-primary"
                            iconSize={rem(10)}
                            minWidth={rem(24)}
                            width={rem(24)}
                            height={rem(24)}
                            marginRight={rem(8)}
                          />
                        }
                      >
                        <ActionList
                          emptyListComponent={
                            <DroppableContainer
                              id="captureList"
                              payload={{
                                containerId: 'captureList',
                                category,
                              }}
                            >
                              <EmptyActionList
                                title={
                                  selectedSegmented !== ALL_ACTIONS && !isEmpty(category.plannedActions)
                                    ? t('emptyStates.allActionsPlanned')
                                    : t('emptyStates.allActionsCompleted')
                                }
                                motivational="small"
                              />
                            </DroppableContainer>
                          }
                          isEmpty={isEmpty(
                            filterIncompleteActions(category.incompleteActions, startDate, 'week', endDate),
                          )}
                        >
                          <SortableContext
                            items={filterIncompleteActions(category.incompleteActions, startDate, 'week', endDate)}
                          >
                            {filterIncompleteActions(category.incompleteActions, startDate, 'week', endDate).map(
                              (action, index) => (
                                // Plain div (not motion.div): the per-card whileInView fade
                                // created one IntersectionObserver per row, hundreds at once on
                                // a busy week — the dominant cost of the load freeze. Cosmetic only.
                                <div key={action.id}>
                                  <ActionListItem
                                    item={action}
                                    index={index}
                                    onClickCreateBlock={onCreateBlockFromAction}
                                    dndPayload={{
                                      containerId: 'captureList',
                                      categoryId: action.categoryId,
                                      category,
                                      projectId: action?.projectId,
                                    }}
                                    recurringWeekContext={recurringWeekContext}
                                    disableRecurringStatusChanges={validateRecurrence(action.event?.recurrence) ? true : false}
                                  />
                                </div>
                              ),
                            )}
                          </SortableContext>
                        </ActionList>
                        <ActionListStatusGroup
                          className={`${category.id}-completed-actions`}
                          actions={category.completedActions}
                          title={t('actionGroups.completedActions')}
                          emptyMessage={t('actionGroups.noCompletedActions')}
                          onClickCreateBlock={onCreateBlockFromAction}
                          recurringWeekContext={recurringWeekContext}
                        />
                      </CaptureList>
                      {category.blocks.sort(sortBlocksByCompletion).map((block, index) => (
                        <DroppableContainer
                          style={{ marginBottom: rem(16) }}
                          id={block.key}
                          key={block.id}
                          payload={{
                            containerId: block.key,
                            blockId: block.id,
                            category: block.category,
                            projectId: block?.projectId,
                          }}
                        >
                          <div>
                            {/* Plain div (not motion.div): same per-card whileInView cost as above. */}
                            <DnDBlockItem
                              key={block.id}
                              actions={block?.actions ?? []}
                              block={block}
                              index={index}
                              weeklyPlanId={weeklyPlanId}
                              onCompleteBlock={onCompleteBlock}
                              onSelectBlock={(options) => {
                                setSelectedBlock(block);
                                setReScheduleBlock(!!options?.isReschedule);
                              }}
                              openSetUncompletedActionsModal={openSetUncompletedActionsModal}
                              onUncompleteBlock={onUncompleteBlock}
                              onClickRemoveFromBlock={handleRemoveActionFromBlock}
                              onDeleteBlock={handleDeleteBlock}
                              openEditBlockModal={openEditBlockModal}
                              collapsedByDefault={block.isCompleted}
                              openDuplicateBlockModal={openDuplicatedBlockModal}
                              recurringWeekContext={recurringWeekContext}
                            />
                          </div>
                        </DroppableContainer>
                      ))}
                    </Fragment>
                  ))}
                  {notEmptyCategories.length === 0 && <EmptyCategoryCallout />}
                  {categories &&
                    categories.length === 1 &&
                    isUncategorized(categories[0]) &&
                    categories[0].blocks.length === 0 && (
                      <Callout title={t('gettingStarted.title')} backgroundColor="background-tertiary">
                        <Text textStyle="small">{t('gettingStarted.description')}</Text>
                        <Text
                          sx={{
                            'span:not(.block)': {
                              opacity: '0.5',
                            },
                            '.block': {
                              display: 'inline-flex',
                              gap: rem(4),
                              verticalAlign: 'top',
                              marginRight: rem(8),
                            },
                          }}
                          textStyle="small"
                          marginTop={rem(14)}
                          color="text-secondary"
                        >
                          <span>{t('gettingStarted.instructionPart1')}</span>
                          <Icon as={IconPlus} boxSize={rem(14)} verticalAlign="top" marginX={rem(4)} />
                          <span style={{ marginRight: rem(8) }}>{t('gettingStarted.instructionPart2')}</span>
                          <span className="block">
                            <Icon as={IconCategories} boxSize={rem(14)} /> {t('gettingStarted.instructionPart3')}
                          </span>
                          <span>{t('gettingStarted.instructionPart4')}</span>
                        </Text>
                      </Callout>
                    )}
                  <Accordion allowMultiple defaultIndex={[-1]}>
                    <EmptyCategories
                      viewportRef={actionsContainerRef}
                      openCreateBlockModal={openCreateBlockModal}
                      setActionModalOpen={setActionModalOpen}
                      setTempCategoryId={setTempCategoryId}
                    />
                  </Accordion>
                </DroppableContainer>
                {ItemOverlay}
              </Flex>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence initial={false}>
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              variants={PLANNER_VARIANTS}
              animate="default"
              exit={{
                width: 0,
                opacity: 0,
                transition: {
                  duration: 0.5,
                  when: 'afterChildren',
                  staggerChildren: 0.1,
                },
              }}
              style={{
                height: 'inherit',
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
            >
              {!isDailyPlanPage && (
                <WeeklyCalendar
                  onChangeEventTime={handleChangeEventTime}
                  onRemoveTime={handleRemoveEventTime}
                  onUnplan={unplanAction}
                  events={events}
                  isPlannerExpanded={isCalendarExpanded}
                  onClickEvent={onClickEvent}
                />
              )}

              {isDailyPlanPage && (
                <DroppableContainer id="timeline" height="full">
                  <CalendarProvider date={selectedDate} view="day">
                    {/* 
                    unplanTimelineEvent and onClickTimelineEvent are basically
                    duplicates of unplanAction and onClickEvent, but they are
                    needed because the weekly calendar is not using the same
                    data structure as the daily calendar.
                    TODO: unify data structure of weekly and daily calendars 
                    */}
                    <Timeline onClickEvent={onClickTimelineEvent} onUnplan={setTimelineEventToUnplan} />
                  </CalendarProvider>
                </DroppableContainer>
              )}
            </motion.div>
          </AnimatePresence>
        </Flex>
      </DndContainer>

      {isOpenDuplicateBlockModal && !isNull(selectedBlock) && (
        <DuplicateBlockModal
          reScheduleBlock={reScheduleBlock}
          block={selectedBlock}
          isOpen={isOpenDuplicateBlockModal}
          onClose={onCloseDuplicatedBlockModal}
        />
      )}

      {isOpenEditBlockModal && !isNull(selectedBlock) && (
        <EditBlockModal
          isOpen
          block={selectedBlock}
          weeklyPlanId={weeklyPlanId}
          onUpdateBlock={onUpdateBlock}
          onDeleteBlock={handleDeleteBlock}
          onCancel={onCloseEditBlockModal}
        />
      )}

      {isOpenSetUncompletedActionsModal && selectedBlock && (
        <UncompletedActionsModal
          blockId={selectedBlock.id}
          completedActions={selectedBlock?.actions?.filter((action) => action.progressStatus === COMPLETE) ?? []}
          isOpen={isOpenSetUncompletedActionsModal}
          onClose={onCloseSetUncompletedActionsModal}
          onDeleteBlock={({ id }) => deleteBlock.mutate({ blockId: id })}
          onCompleteBlockActions={onCompleteBlockActions}
          onRemoveBlockActions={onRemoveBlockActions}
          onCompleteBlock={onCompleteBlock}
        />
      )}

      {isOpenDeleteBlockModal && (
        <DeleteBlockModal isOpen={isOpenDeleteBlockModal} onClose={onCloseDeleteBlockModal} onSubmit={onDeleteBlock} />
      )}

      {isOpenConfirmDeleteBlockModal && (
        <ConfirmDeleteBlockModal
          isOpen={isOpenConfirmDeleteBlockModal}
          onClose={onCloseConfirmDeleteBlockModal}
          onSubmit={onDeleteBlock}
        />
      )}

      {isOpenCreateBlockModal && data && (
        <CreateBlockModal
          isOpen={isOpenCreateBlockModal}
          onClose={onCloseAndResetCreateBlockModal}
          scheduledDate={selectedDate}
          projectId={tempProjectId ?? ''}
          categoryId={tempCategoryId ?? ''}
          actionId={tempBlockActionId ?? ''}
        />
      )}

      {isScheduleEventModalOpen && (
        <ScheduleEventModal isOpen onSave={handleDailyActionDropped} onClose={onCloseScheduleEventModal} />
      )}

      {isEditRecurringEvent && (
        <RecurringEventModal
          isOpen={isEditRecurringEvent}
          onCancel={handleCloseEditRecurringAction}
          onConfirm={editRecurringEvents}
          event={eventToUnplan}
          isSameDay={false}
          mode="edit"
          onlyThisOccurrenceEdit
        />
      )}

      {isDeleteRecurringEvents && (
        <RecurringEventModal
          isOpen={isDeleteRecurringEvents}
          onCancel={handleCloseDeleteDuplicatedAction}
          onConfirm={deleteRecurringEvents}
          event={eventToUnplan}
          isSameDay={true}
        />
      )}

      {showCanceledModal && selectedCategoryForBulk && (
        <DeleteBulkActionsModal
          isOpen={showCanceledModal}
          onClose={() => setShowCanceledModal(false)}
          actions={selectedCategoryForBulk.incompleteActions}
          mode={modeValues.canceled}
          recurringDurationRange={captureDurationRange ?? undefined}
        />
      )}
      {showDeleteModal && selectedCategoryForBulk && (
        <DeleteBulkActionsModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          actions={selectedCategoryForBulk.incompleteActions}
          mode={modeValues.delete}
          recurringDurationRange={captureDurationRange ?? undefined}
        />
      )}

      <RecurringInBlockModal isOpen={isOpenRecurringInBlockModal} onClose={closeRecurringInBlockModal} />
    </>
  );
}

export default MyWeeklyPlan;
